/**
 * KURULUM — `KURULUM_GEREKLI` (kilitli defter, Adım 1).
 *
 * İki durum: bu cihazda hiç defter yok (yeni başlangıç) ya da şifresiz eski
 * defter taşınmayı bekliyor (göç — kullanıcı kararı K4: zorunlu, atlanamaz).
 * Sıra: parola → Worker kurar (eski defter varsa doğrulayarak taşır) →
 * 24 kelime gösterilir ve doğrulatılır → defter açılır.
 *
 * İfade doğrulanmadan sekme kapanırsa profil kurulmuş olur; sonraki açılışta
 * kilit ekranı gelir ve uygulama içinde "ifade doğrulanmadı" bandı durur
 * (`KilitBandi`), oradan yeni ifade alınır.
 */

import { M } from "../../dil";
import { BuluttanGetir } from "./BuluttanGetir";
import { DEPOLAMA_SORUSU_AYARI } from "../DepolamaSorusu";
import { useEffect, useState } from "react";
import type { KilitDurumu, LocalRepository } from "@parafon/contracts/local-api";
import { useMutation } from "../../repo/useAsync";
import { Button, Field, InlineError, Notice, Spinner, TextInput } from "../../ui/primitives";
import { IfadeGosterVeDogrula } from "./IfadeGosterVeDogrula";
import { ParolaAlanlari, parolaSorunu, type ParolaDegeri } from "./ParolaAlanlari";

/** Uygulama ayarlarında "ifade doğrulandı" işareti (şifreli defterin içinde). */
export const IFADE_DOGRULANDI_AYARI = "kilit.ifadeDogrulandi";

export function KurulumEkrani({
  repo,
  onBitti,
  yeniProfil,
  getir,
}: {
  readonly repo: LocalRepository;
  readonly onBitti: () => void;
  /** ÇOKLU PROFİL: var olan profillerin yanına yeni profil; `onVazgec` kilit ekranına döner. */
  readonly yeniProfil?: { readonly onVazgec: () => void };
  /** "Buluttan getir" için; testler sahte bulut geçirir. */
  readonly getir?: Omit<Parameters<typeof BuluttanGetir>[0], "repo" | "onBitti">;
}) {
  const [etiket, setEtiket] = useState("");
  // "Yeni kasa kur" ya da "Buluttan getir" (kullanıcı kararı 19.10.2026).
  const [yol, setYol] = useState<"yeni" | "getir">("yeni");
  const kilit = repo.kilit;
  const [durum, setDurum] = useState<KilitDurumu | null>(null);
  const [deger, setDeger] = useState<ParolaDegeri>({ parola: "", tekrar: "" });
  const [sorun, setSorun] = useState<string | null>(null);
  const [kurulum, setKurulum] = useState<{ kelimeler: readonly string[]; tasinan: number | null } | null>(null);
  const kur = useMutation();
  const bitir = useMutation();

  useEffect(() => {
    void kilit?.durum().then(setDurum, () => setDurum(null));
  }, [kilit]);

  if (kilit === undefined) return null;
  const tasima = yeniProfil === undefined && durum?.sifresizDefterVar === true;

  const onKur = (): void => {
    const s = yeniProfil !== undefined && etiket.trim().length === 0 ? M.kilit.kurulum.profileAdVerin : parolaSorunu(deger);
    setSorun(s);
    if (s !== null) return;
    void kur.run(async () => {
      const k = await kilit.kur(deger.parola, yeniProfil === undefined ? undefined : etiket.trim());
      setKurulum({ kelimeler: k.kelimeler, tasinan: k.tasinanHareket });
      setDeger({ parola: "", tekrar: "" });
    });
  };

  const onDogrulandi = (): void => {
    void bitir.run(async () => {
      await repo.open();
      await repo.settings.set(IFADE_DOGRULANDI_AYARI, "1");
      // "Kasanız nerede dursun?" ana sayfada, cevaplanana kadar (19.10.2026).
      await repo.settings.set(DEPOLAMA_SORUSU_AYARI, "bekliyor");
      onBitti();
    });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center p-6">
      <div className="pf-card space-y-4 p-5">
        <h1 className="text-[18px] font-semibold text-ink">
          {yeniProfil !== undefined ? M.kilit.kurulum.yeniProfil : tasima ? M.kilit.kurulum.sifreleyelim : M.kilit.kurulum.kuralim}
        </h1>

        {kurulum === null && !tasima && kilit.yedektenKur !== undefined && (
          <fieldset className="grid gap-2 text-[13px] sm:grid-cols-2">
            <legend className="sr-only">{M.kilit.kurulum.nasilBaslayalim}</legend>
            <label className={`flex gap-2 rounded-pf border p-2 ${yol === "yeni" ? "border-accent bg-accent-soft" : "border-line"}`}>
              <input type="radio" name="kurulum-yol" checked={yol === "yeni"} onChange={() => setYol("yeni")} />
              <span>
                <span className="font-semibold text-ink">{M.kilit.kurulum.yeniKasaKur}</span>
                <span className="block text-[12px] text-muted">{M.kilit.kurulum.yeniKasaKurAciklama}</span>
              </span>
            </label>
            <label className={`flex gap-2 rounded-pf border p-2 ${yol === "getir" ? "border-accent bg-accent-soft" : "border-line"}`}>
              <input type="radio" name="kurulum-yol" checked={yol === "getir"} onChange={() => setYol("getir")} />
              <span>
                <span className="font-semibold text-ink">{M.kilit.kurulum.buluttanGetir}</span>
                <span className="block text-[12px] text-muted">{M.kilit.kurulum.buluttanGetirAciklama}</span>
              </span>
            </label>
          </fieldset>
        )}

        {kurulum === null && yol === "getir" && !tasima ? (
          <>
            <BuluttanGetir repo={repo} onBitti={onBitti} {...(getir ?? {})} />
            {yeniProfil !== undefined && (
              <Button variant="ghost" onClick={yeniProfil.onVazgec}>
                {M.ortak.vazgec}
              </Button>
            )}
          </>
        ) : kurulum === null ? (
          <>
            <p className="text-[13px] leading-relaxed text-ink">
              {tasima
                ? M.kilit.kurulum.tasimaAciklama
                : M.kilit.kurulum.yeniAciklama}
            </p>
            <p className="text-[13px] leading-relaxed text-muted">{M.kilit.kurulum.parolaKorur}</p>
            {durum === null && <Spinner label={M.kilit.kurulum.durumOkunuyor} />}
            {yeniProfil !== undefined && (
              <>
                <p className="text-[13px] leading-relaxed text-ink">{M.kilit.kurulum.profilAyriAciklama}</p>
                <Field label={M.kilit.profilAdi} htmlFor="kurulum-etiket" hint={M.kilit.kurulum.profilAdiIpucu}>
                  <TextInput
                    id="kurulum-etiket"
                    maxLength={40}
                    value={etiket}
                    onChange={(e) => setEtiket(e.currentTarget.value)}
                  />
                </Field>
              </>
            )}
            <ParolaAlanlari deger={deger} onDegis={setDeger} />
            {sorun !== null && <InlineError error={new Error(sorun)} />}
            <Button variant="primary" disabled={kur.pending} onClick={onKur}>
              {kur.pending ? (tasima ? M.kilit.kurulum.sifreleniyor : M.kilit.kurulum.kuruluyor) : tasima ? M.kilit.kurulum.sifreleVeTasi : M.kilit.kurulum.kur}
            </Button>
            {kur.error !== undefined && <InlineError error={kur.error} />}
            {yeniProfil !== undefined && (
              <Button variant="ghost" disabled={kur.pending} onClick={yeniProfil.onVazgec}>
                {M.ortak.vazgec}
              </Button>
            )}
          </>
        ) : (
          <>
            {kurulum.tasinan !== null && (
              <Notice tone="info" title={M.kilit.kurulum.sifrelendiBaslik}>
                <p>{M.kilit.kurulum.sifrelendi(kurulum.tasinan)}</p>
              </Notice>
            )}
            <h2 className="text-[14px] font-semibold text-ink">{M.kilit.kurulum.kurtarmaIfadeniz}</h2>
            <IfadeGosterVeDogrula kelimeler={kurulum.kelimeler} onDogrulandi={onDogrulandi} />
            {bitir.pending && <Spinner label={M.kilit.kurulum.kasaAciliyor} />}
            {bitir.error !== undefined && <InlineError error={bitir.error} />}
          </>
        )}
      </div>
    </main>
  );
}
